import { action, computed, observable, runInAction, toJS } from 'mobx';
import { FetchStatus } from 'servicesCommon/apiTypes';
import { RootStore } from 'AppClient/stores';
import { GoogleAddressAndFlightData, PersonalUserDetails } from 'app-types';
import { addressesApi } from 'AppClient/api/Addresses';
import { removeEmptyFromArray, removeEmptyProperties, isLoading } from 'utils/utils';
import { hasFlight, isFlightItem, isFlightAddress } from 'utils/flightsUtils';
import { dateTimeZone } from 'utils/converters';
import { AddressPoints } from 'AppClient/api/Bookings';

const FE_VALIDATION_ERROR = 'address not found, select from the suggested list to continue';

export interface ReceiveGoogleAddressesOptions {
	label: string;
	isPickup?: boolean;
	date?: string;
}

export type ReceiveGoogleAddressesFunc = (
	searchText: string,
	opt: ReceiveGoogleAddressesOptions
) => void;

export class AddressesStore {
	@observable public addressPoints: Array<AddressPoints | undefined> = [];

	@observable
	private _airportTypes: {
		[label: string]: string;
	} = {};
	@observable
	private _addresses: {
		[label: string]: GoogleAddressAndFlightData | string | undefined;
	} = {};
	private _resetedFields: string[] = [];
	@observable
	private _addressesSuggestions: {
		[label: string]: GoogleAddressAndFlightData[];
	} = {};
	@observable
	private _validationAddressErrors: {
		[label: string]: string[];
	} = {};
	@observable private _addressDetails?: PersonalUserDetails;

	@observable private _fetchStatus: {
		[label: string]: FetchStatus;
	} = {};
	@observable private _validateAddressFetchStatus: {
		[label: string]: FetchStatus;
	} = {};
	@observable private _isSameFlightError: boolean = false;

	constructor(private rootStore: RootStore) {}

	@action.bound
	addAddressPoint(index: number, addressPoint: AddressPoints | null) {
		const addressPoints = [...toJS(this.addressPoints)];
		if (addressPoint === null) {
			addressPoints.splice(index, 1, undefined);
		} else {
			addressPoints[index] = addressPoint;
		}
		this.addressPoints = addressPoints;
		this.rootStore.newBookingsStore.updatePricesPayload({
			addressPoints: this.getAllAddressesPoints(),
		});
	}

	@action.bound
	resetAddressPoints() {
		this.addressPoints = [];
	}

	getAllAddressesPoints = (): AddressPoints[] => {
		return removeEmptyFromArray(toJS(this.addressPoints), false);
	};

	isNoValidationErrors = () => {
		let hasError = true;
		const errors = this.validationAddressErrors;
		Object.keys(errors).forEach(key => {
			const errorItem = errors[key];
			if (errorItem && errorItem.length !== 0) {
				hasError = false;
			}
		});
		return hasError;
	};

	@action.bound
	noResetedLocations = () => {
		let result = true;
		Object.entries(this.addresses).forEach(([key]) => {
			if (this.resetedFields.includes(key)) {
				result = false;
			}
		});
		return result;
	};

	@computed
	get isValidAddresses(): boolean {
		const addresses = Object.values(this._addresses).filter(item => item !== undefined);
		return (
			addresses.length >= 2 &&
			this.isNoValidationErrors() &&
			this.noResetedLocations() &&
			!this._isSameFlightError &&
			!this.isAnyValidateAddressLoading
		);
	}

	nullishStringAddress = (
		address: GoogleAddressAndFlightData | string | undefined
	): GoogleAddressAndFlightData | undefined => {
		if (typeof address === 'string') {
			return undefined;
		}
		return address;
	};

	@computed
	get addressesArray(): Array<GoogleAddressAndFlightData | undefined> {
		return Object.values(this._addresses)
			.filter(item => item !== undefined)
			.map(this.nullishStringAddress);
	}

	getFlightNumber(item: GoogleAddressAndFlightData) {
		let flightNumber = undefined;
		if (isFlightAddress(item)) {
			flightNumber = item.flight ? item.flight.flightNumber : undefined;
		}
		if (isFlightItem(item)) {
			flightNumber = item.flightNumber;
		}
		return flightNumber;
	}

	@action
	checkSameFlightError() {
		let isSameFlightError = false;
		let flightNumber1: string | undefined, flightNumber2: string | undefined;
		const arr = this.addressesArray;
		arr.forEach((item: GoogleAddressAndFlightData | undefined, index: number) => {
			if (!isSameFlightError && item) {
				flightNumber1 = this.getFlightNumber(item);

				arr.forEach((item2: GoogleAddressAndFlightData | undefined, index2: number) => {
					if (item2 && index !== index2) {
						flightNumber2 = this.getFlightNumber(item2);

						if (flightNumber1 && flightNumber2 && flightNumber1 === flightNumber2) {
							isSameFlightError = true;
						}
					}
				});
			}
		});

		if (isSameFlightError) {
			this._isSameFlightError = true;
			this.rootStore.alertStore.setErrorAlert(
				'The same flight number cannot be used for two or more locations.'
			);
		} else {
			this._isSameFlightError = false;
			this.rootStore.alertStore.clearAlert();
		}
	}

	@computed
	get airportTypes() {
		return this._airportTypes;
	}
	@computed
	get addresses() {
		return this._addresses;
	}
	@computed
	get addressesFilled() {
		return removeEmptyProperties(this._addresses);
	}
	@computed
	get resetedFields() {
		return this._resetedFields;
	}
	@computed
	get addressDetails() {
		return this._addressDetails;
	}

	@computed
	get isAddressLoading() {
		return Object.values(this._fetchStatus).some(item => isLoading(item));
	}

	@computed
	get isAnyValidateAddressLoading() {
		return Object.values(this._validateAddressFetchStatus).some(item => isLoading(item));
	}

	@action.bound
	isValidateAddressLoading(label: string): boolean {
		return isLoading(this._validateAddressFetchStatus[label]);
	}

	@action.bound
	setAddress(address: GoogleAddressAndFlightData | string | undefined, label: string) {
		this._addresses = {
			...this._addresses,
			[label]: address,
		};
		this.checkSameFlightError();
	}

	@action.bound
	addResetedField(label: string) {
		this._resetedFields = [...this._resetedFields, label];
	}

	@action.bound
	removeResetedField(label: string) {
		this._resetedFields = this._resetedFields.filter(item => item !== label);
	}

	@action.bound
	setAddresses(addresses: { [label: string]: GoogleAddressAndFlightData | string | undefined }) {
		this._addresses = addresses;
		this.checkSameFlightError();
	}

	@action.bound
	setValidationAddressError(label: string, errorsArray: string[]) {
		this._validationAddressErrors = {
			...this._validationAddressErrors,
			[label]: errorsArray,
		};
	}

	@action.bound
	setAddressSuggestions(label: string, suggestions: GoogleAddressAndFlightData[]) {
		this._addressesSuggestions = {
			...this._addressesSuggestions,
			[label]: suggestions,
		};
	}

	@action.bound
	setAirportType(airportType: string, label: string) {
		this._airportTypes = {
			...this._airportTypes,
			[label]: airportType,
		};
	}

	@action.bound
	clearAllAddressesData() {
		runInAction(() => {
			this._addresses = {};
			this._addressesSuggestions = {};
			this._validationAddressErrors = {};
			this._airportTypes = {};
			this._addressDetails = undefined;
			this._resetedFields = [];
		});
	}

	@computed
	get addressesSuggestions() {
		return this._addressesSuggestions;
	}

	@computed
	get validationAddressErrors() {
		return toJS(this._validationAddressErrors);
	}

	// google adresses

	getGoogleAddressByPlaceId(placeId: string, label: string) {
		if (this._addressesSuggestions[label]) {
			return (
				this._addressesSuggestions[label].find(item => {
					if (isFlightItem(item)) {
						return placeId === item.departurePlaceId || placeId === item.arrivalPlaceId;
					} else {
						return placeId === item.placeId;
					}
				}) || ({} as GoogleAddressAndFlightData)
			);
		}
		return {} as GoogleAddressAndFlightData;
	}

	@computed
	get isFlightSelectedInFirstLocation() {
		let index = -1;
		for (const [, addressItem] of Object.entries(this._addresses)) {
			index++;
			if (typeof addressItem === 'string') {
				return false;
			}
			if (hasFlight(addressItem) && index === 0) {
				// case of amending/rebooking
				return true;
			}
			if (addressItem) {
				// case of booking
				return isFlightItem(addressItem) && index === 0;
			}
		}
		return false;
	}

	@action.bound
	resetSingleLocation(
		label: string,
		withAddress?: GoogleAddressAndFlightData,
		withoutResetingField?: boolean
	) {
		this.setAddress(withAddress || undefined, label);
		if (!withoutResetingField) {
			// simple removing value shouldn't block from validation
			this.addResetedField(label);
		}
		this.setValidationAddressError(label, []);
		this.setAddressSuggestions(label, []);
		this._airportTypes = {
			...this._airportTypes,
			[label]: '',
		};
	}

	@action.bound
	resetSuggestions(label?: string) {
		if (label) {
			this.setAddressSuggestions(label, []);
		} else {
			this._addressesSuggestions = {};
		}
	}

	@action.bound
	async receiveGoogleAddresses(searchText: string, opt: ReceiveGoogleAddressesOptions) {
		const { label, isPickup, date } = opt;
		if (!searchText) {
			this.resetSingleLocation(label, undefined, true);
		} else {
			try {
				this._fetchStatus = {
					...this._fetchStatus,
					[searchText]: FetchStatus.PENDING,
				};
				this.setAddressSuggestions(label, []);

				const response = await addressesApi.getAddressSuggestions({
					searchText,
					pickUpDate: date,
					isPickup,
				});

				const { result, error } = response.data;
				if (error) {
					this._fetchStatus = {
						...this._fetchStatus,
						[searchText]: FetchStatus.FAILURE,
					};
					this.rootStore.alertStore.setErrorAlert(error);
					this.setAddressSuggestions(label, []);
					return;
				}
				if (result) {
					const { addressSuggestions, flightData } = result;
					const allAddresses = addressSuggestions || [];
					const allFlights = flightData || [];
					runInAction(() => {
						this._fetchStatus = {
							...this._fetchStatus,
							[searchText]: FetchStatus.SUCCESS,
						};
						if (!this.isAddressLoading) {
							this.setAddressSuggestions(label, [...allAddresses, ...allFlights]);
						}
					});
				}
			} catch (error) {
				this.rootStore.alertStore.setRequestError(error);

				this._fetchStatus = {
					...this._fetchStatus,
					[searchText]: FetchStatus.FAILURE,
				};
			}
		}
	}

	@action.bound
	async validateGoogleAddress(
		placeId: string,
		label: string,
		isFEValidation?: boolean,
		needDetails?: boolean,
		location?: GoogleAddressAndFlightData,
		airportType?: string
	) {
		this.removeResetedField(label);

		if (isFEValidation) {
			// validate case when user inputs something, but doesn't choose any suggestion
			this.setValidationAddressError(label, [FE_VALIDATION_ERROR]);
		} else {
			try {
				let addressDetailsError = null;
				let addressDetailsResult: PersonalUserDetails | null;
				this._fetchStatus = {};
				this._validateAddressFetchStatus = {
					...this._validateAddressFetchStatus,
					[label]: FetchStatus.PENDING,
				};
				const response = await addressesApi.validateGoogleAddress({
					placeId,
				});
				const { result, error } = response.data;

				if (needDetails) {
					const addressDetailsResponse = await addressesApi.getAddressDetails({
						placeId,
					});
					addressDetailsResult = addressDetailsResponse.data.result;
					addressDetailsError = addressDetailsResponse.data.error;
				}
				const commonError = error || addressDetailsError;
				if (commonError) {
					this._validateAddressFetchStatus = {
						...this._validateAddressFetchStatus,
						[label]: FetchStatus.FAILURE,
					};
					this.rootStore.alertStore.setErrorAlert(commonError);
					return;
				}
				if (result) {
					const { entities } = result;
					const { updatePricesPayload } = this.rootStore.newBookingsStore!;
					if (entities) {
						runInAction(() => {
							this._validateAddressFetchStatus = {
								...this._validateAddressFetchStatus,
								[label]: FetchStatus.SUCCESS,
							};

							this.setValidationAddressError(label, entities.length ? entities : []);
							this.setAddress(this.getGoogleAddressByPlaceId(placeId, label), label);
							if (airportType) {
								this.setAirportType(airportType, label);
							}
							if (location && isFlightItem(location)) {
								updatePricesPayload({
									pickupDateTimeInLocationTimeZone: dateTimeZone(
										location.arrivalDate
									) as string,
								});
							}

							if (addressDetailsResult) {
								this._addressDetails = addressDetailsResult;
							}
						});
					}
				}
			} catch (error) {
				this.rootStore.alertStore.setRequestError(error);
				this._validateAddressFetchStatus = {
					...this._validateAddressFetchStatus,
					[label]: FetchStatus.FAILURE,
				};
			}
		}
	}
}
